// Serie video "Strumenti derivati" (IT) / "Derivatives" (EN): schede degli episodi PUBBLICATI.
//
// Regola (decisione 26/09/2026): YouTube è il master delle date. Un episodio entra qui solo
// DOPO l'uscita nella relativa lingua: niente segnaposto, niente "in arrivo". Se un episodio è
// uscito in una lingua sola, si valorizza solo quella chiave (it / en).
// Programmati al 15/10/2026: IT Ep 14 (24/10), IT Ep 4 (21/11), EN Ep 13 (17/10), EN Ep 14 (31/10).
//
// Contenuto educativo: nessuna indicazione su strumenti da comprare o vendere. Gli esempi
// numerici sono ipotetici e verificati (script di controllo nella sessione del 15/10/2026).
// Capitoli di Hull: numerazione della 10ª edizione di "Options, Futures and Other Derivatives".

export const DERIVATI_PLAYLIST = {
  it: "PL4Ivcohv28tL4Y3JRgC9L6NsQElpKSyQH",
  en: "PL4Ivcohv28tLV6Sd958sbo8rER0-onx5I",
};

export const DERIVATI_GROUPS = [
  {
    key: "base",
    it: { title: "Fondamenta e futures", intro: "Che cosa sono i derivati, come funziona un future e come lo si usa per coprire un rischio. È la base di tutto il resto della serie." },
    en: { title: "Foundations and futures", intro: "What derivatives are, how a futures contract works and how it is used to hedge a risk. Everything else in the series builds on this." },
  },
  {
    key: "tassi",
    it: { title: "Tassi d'interesse e swap", intro: "Curve dei tassi, futures sui titoli di Stato e swap: gli strumenti con cui banche, imprese e gestori trattano il rischio di tasso." },
    en: { title: "Interest rates and swaps", intro: "Rate curves, bond futures and swaps: the tools banks, companies and portfolio managers use to handle interest-rate risk." },
  },
  {
    key: "opzioni",
    it: { title: "Opzioni", intro: "Call e put, i limiti logici al loro prezzo e le strategie che combinano più opzioni tra loro o con il sottostante." },
    en: { title: "Options", intro: "Calls and puts, the logical limits to their prices and the strategies that combine several options, or options and the underlying." },
  },
  {
    key: "pricing",
    it: { title: "Modelli di prezzo", intro: "Dall'albero binomiale a Black-Scholes: come si arriva a dare un prezzo a un'opzione e che cosa racconta la volatilità implicita." },
    en: { title: "Pricing models", intro: "From the binomial tree to Black-Scholes: how an option gets a price and what implied volatility tells you." },
  },
  {
    key: "deep",
    it: { title: "Approfondimenti", intro: "Due puntate più lunghe, fuori dalla numerazione: un ripasso avanzato su futures e opzioni e una guida a certificati e covered warrant." },
    en: { title: "Deep dives", intro: "Two longer episodes outside the numbering: an advanced review of futures and options and a guide to certificates and covered warrants." },
  },
];

// n: numero dell'episodio (null per gli approfondimenti), id: ancora della scheda nella pagina.
export const DERIVATI_EPISODES = [
  // ───────────────────────────── FONDAMENTA E FUTURES ─────────────────────────────
  {
    n: 1, id: "ep-1", group: "base",
    it: {
      videoId: "L3JzHjZ6X2Q",
      title: "Derivati spiegati semplice: copertura, speculazione, arbitraggio",
      summary: "Che cos'è un derivato, chi lo usa e perché nasce come assicurazione, non come scommessa.",
      learn: [
        "Perché un derivato trasferisce un rischio da chi non lo vuole a chi è disposto a prenderlo",
        "La differenza tra mercato regolamentato e OTC, e il ruolo della clearing house",
        "I tre usi: copertura, speculazione, arbitraggio",
      ],
      concepts: [
        "Sottostante: il valore del contratto dipende da un'altra grandezza, che sia un'azione, un indice, un tasso, una valuta o una materia prima.",
        "Standardizzazione: in borsa i contratti hanno taglio, scadenze e regole fisse, ed è per questo che passano di mano facilmente.",
        "Clearing house: si mette in mezzo tra compratore e venditore e garantisce che il contratto venga onorato. Nell'OTC questo garante manca e il rischio di controparte resta a chi firma.",
        "Arbitraggio: chi sfrutta un disallineamento di prezzo lo chiude. Per questo le finestre durano pochissimo.",
      ],
      example: "Un'azienda italiana che fra sei mesi incasserà un milione di dollari può fissare oggi il cambio con un contratto a termine. Se il dollaro si indebolisce non perde nulla; se si rafforza rinuncia al guadagno. Ha comprato certezza, non un profitto.",
      risks: "L'errore più diffuso è giudicare lo strumento dall'uso che se ne fa. Lo stesso contratto riduce il rischio di un esportatore e moltiplica quello di uno speculatore che opera a leva.",
      ref: "Hull, cap. 1",
      kb: { slug: "derivati-spiegati-semplice", label: "Derivati: cosa sono e come funzionano" },
    },
    en: {
      videoId: "L-JmzID5D0U",
      title: "Derivatives explained: hedging, speculation, arbitrage",
      summary: "What a derivative is, who uses it and why it started out as insurance rather than a bet.",
      learn: [
        "Why a derivative moves risk from those who don't want it to those willing to take it",
        "The difference between exchange-traded and OTC markets, and the role of the clearing house",
        "The three uses: hedging, speculation, arbitrage",
      ],
      concepts: [
        "Underlying: the contract's value depends on something else, whether a stock, an index, an interest rate, a currency or a commodity.",
        "Standardisation: on an exchange, contract size, expiry dates and rules are fixed, which is what makes contracts easy to trade.",
        "Clearing house: it stands between buyer and seller and guarantees the contract is honoured. In OTC markets there is no such guarantor, and counterparty risk stays with whoever signs.",
        "Arbitrage: whoever exploits a price misalignment also closes it. That is why the windows are so short.",
      ],
      example: "An Italian company that will receive one million dollars in six months can lock in the exchange rate today with a forward contract. If the dollar weakens it loses nothing; if it strengthens it gives up the gain. It has bought certainty, not a profit.",
      risks: "The most common mistake is to judge the instrument by how it is used. The same contract reduces an exporter's risk and multiplies the risk of a leveraged speculator.",
      ref: "Hull, ch. 1",
      kb: { slug: "derivati-spiegati-semplice", label: "Derivatives: what they are and how they work" },
    },
  },
  {
    n: 2, id: "ep-2", group: "base",
    it: {
      videoId: "zvbVoKEoZIY",
      title: "Futures: margini, mark-to-market e consegna",
      summary: "Come funziona la macchina dei futures: margini, regolamento quotidiano di guadagni e perdite, consegna.",
      learn: [
        "Perché il future non si regge sulla fiducia tra le parti ma su un meccanismo",
        "Che cosa sono margine iniziale, margine di mantenimento e margin call",
        "Come il mark-to-market quotidiano spezza una perdita finale in tante piccole perdite giornaliere",
      ],
      concepts: [
        "Il margine non è un prezzo pagato. È una garanzia che la clearing house trattiene a fronte della posizione.",
        "Ogni sera la posizione viene rivalutata al prezzo di chiusura e la differenza passa da un conto all'altro.",
        "Se il conto scende sotto il margine di mantenimento arriva la margin call: va riportato al margine iniziale, altrimenti la posizione viene chiusa.",
        "La consegna fisica è rara. Quasi tutte le posizioni si chiudono prima della scadenza.",
      ],
      example: "Esempio ipotetico: future su un indice a 30.000 punti con moltiplicatore 1 €, quindi un controvalore di 30.000 € e un margine iniziale del 10%, cioè 3.000 €. Un calo dell'indice del 3% costa 900 €, il 30% del margine versato. Con un margine di mantenimento di 2.250 € basta un calo del 2,5% per ricevere la margin call.",
      risks: "La leva lavora nei due sensi e la perdita può superare il margine versato. Chi non ha liquidità per reintegrarlo rischia di vedersi chiudere la posizione nel momento peggiore.",
      ref: "Hull, cap. 2",
      kb: { slug: "derivati-futures-come-funzionano", label: "Futures, parte 1: come funzionano" },
    },
    en: {
      videoId: "oR3rjG_maCI",
      title: "Futures mechanics: margins, mark-to-market, delivery",
      summary: "How the futures machine works: margins, daily settlement of gains and losses, delivery.",
      learn: [
        "Why futures rely on a mechanism rather than on trust between the parties",
        "What initial margin, maintenance margin and margin calls are",
        "How daily mark-to-market splits a final loss into many small daily losses",
      ],
      concepts: [
        "Margin is not a price paid. It is collateral the clearing house holds against the position.",
        "Every evening the position is revalued at the settlement price and the difference moves from one account to the other.",
        "If the account falls below the maintenance margin, a margin call follows: it must be topped back up to the initial margin, or the position is closed.",
        "Physical delivery is rare. Almost all positions are closed before expiry.",
      ],
      example: "Hypothetical example: a futures contract on an index at 30,000 points with a €1 multiplier, so €30,000 of exposure and a 10% initial margin of €3,000. A 3% drop in the index costs €900, 30% of the margin posted. With a maintenance margin of €2,250, a 2.5% drop is enough to trigger a margin call.",
      risks: "Leverage works both ways and the loss can exceed the margin posted. Without the cash to top it up, the position may be closed at the worst moment.",
      ref: "Hull, ch. 2",
      kb: { slug: "derivati-futures-come-funzionano", label: "Futures, part 1: how they work" },
    },
  },
  {
    n: 3, id: "ep-3", group: "base",
    it: {
      videoId: "tc4Jq1dK9fI",
      title: "Coprirsi davvero con i futures: hedge ratio ottimo e basis risk",
      summary: "La copertura perfetta quasi non esiste: come si misura quella possibile e quanto rischio resta.",
      learn: [
        "Che cos'è il basis risk e perché trasforma il rischio di prezzo in un rischio più piccolo ma diverso",
        "Come si calcola l'hedge ratio a varianza minima",
        "Come si copre un portafoglio azionario con i futures su indice usando il beta",
      ],
      concepts: [
        "La base è la differenza tra prezzo a pronti e prezzo del future. Nel giorno in cui la copertura si chiude non è nota in anticipo.",
        "L'hedge ratio ottimo dipende dalla correlazione tra le variazioni del prezzo a pronti e del future, e dalle loro volatilità.",
        "Con un portafoglio azionario il numero di contratti si ricava dal beta del portafoglio rispetto all'indice.",
      ],
      formula: "h^{*} = \\rho\\,\\frac{\\sigma_S}{\\sigma_F} \\qquad N^{*} = h^{*}\\,\\frac{Q_A}{Q_F}",
      example: "Correlazione 0,9, volatilità del prezzo a pronti 2%, del future 2,5%: h* = 0,72. Per un'esposizione di 1.000.000 € e contratti da 50.000 € servono circa 14 contratti. Con il beta: un portafoglio da 1.000.000 € con beta 1,2 richiede 24 contratti da 50.000 €.",
      risks: "Correlazioni e beta cambiano nel tempo, e una copertura tarata sul passato può funzionare male proprio nelle fasi di stress. La copertura riduce la volatilità, non la cancella.",
      ref: "Hull, cap. 3",
      kb: { slug: "derivati-futures-coperture-hedge-ratio", label: "Futures, parte 2: coprirsi davvero" },
    },
    en: {
      videoId: "d4CZDJQc0pY",
      title: "Futures hedging: minimum-variance hedge ratio and basis risk",
      summary: "Perfect hedges hardly exist: how to measure the hedge you can get, and how much risk is left.",
      learn: [
        "What basis risk is and why it turns price risk into a smaller but different risk",
        "How to compute the minimum-variance hedge ratio",
        "How to hedge an equity portfolio with index futures using beta",
      ],
      concepts: [
        "The basis is the difference between the spot price and the futures price. On the day the hedge is closed, it is not known in advance.",
        "The optimal hedge ratio depends on the correlation between spot and futures price changes and on their volatilities.",
        "For an equity portfolio, the number of contracts comes from the portfolio's beta against the index.",
      ],
      formula: "h^{*} = \\rho\\,\\frac{\\sigma_S}{\\sigma_F} \\qquad N^{*} = h^{*}\\,\\frac{Q_A}{Q_F}",
      example: "Correlation 0.9, spot volatility 2%, futures volatility 2.5%: h* = 0.72. For a €1,000,000 exposure and €50,000 contracts, about 14 contracts are needed. With beta: a €1,000,000 portfolio with a beta of 1.2 needs 24 contracts of €50,000.",
      risks: "Correlations and betas change over time, and a hedge calibrated on the past can fail precisely in stressed markets. Hedging reduces volatility; it does not remove it.",
      ref: "Hull, ch. 3",
      kb: { slug: "derivati-futures-coperture-hedge-ratio", label: "Futures, part 2: hedging properly" },
    },
  },
  {
    n: 5, id: "ep-5", group: "base",
    it: {
      videoId: "e_9uJla-cxE",
      title: "L'incertezza costa, bloccare il prezzo è possibile: il cost of carry",
      summary: "Da dove nasce il prezzo di un forward o di un future: il costo di portare il sottostante fino alla scadenza.",
      learn: [
        "Perché il prezzo forward è una conseguenza del no-arbitrage e non una previsione",
        "Come cambia il cost of carry tra azioni e indici, valute e materie prime",
        "Che cosa indica il convenience yield",
      ],
      concepts: [
        "Comprare oggi il sottostante e tenerlo fino alla scadenza deve costare quanto comprarlo a termine. Se non è così, c'è un arbitraggio.",
        "Per un indice il costo di carry è il tasso d'interesse meno il rendimento da dividendi; per una valuta è il differenziale tra i tassi delle due monete.",
        "Per le materie prime contano anche i costi di stoccaggio e il vantaggio di avere il bene in magazzino, che si chiama convenience yield.",
      ],
      formula: "F_0 = S_0\\,e^{(r-q)T}",
      example: "Indice a 100, tasso al 4%, rendimento da dividendi al 2%, scadenza a un anno: F = 100 × e^0,02 ≈ 102,02. Se il future quotasse molto più in alto, converrebbe comprare l'indice e vendere il future; molto più in basso, il contrario.",
      risks: "Confondere il forward teorico con il prezzo di mercato. Costi di transazione, vincoli sul prestito titoli e tassi diversi tra chi presta e chi prende in prestito aprono una banda in cui l'arbitraggio non conviene.",
      ref: "Hull, cap. 5",
      kb: { slug: "copertura-valutaria-etf-hedged-vs-unhedged", label: "Copertura valutaria: ETF hedged e unhedged" },
    },
    en: {
      videoId: "VuqFT2bGT5o",
      title: "The universal language of futures: cost of carry and arbitrage",
      summary: "Where forward and futures prices come from: the cost of carrying the underlying to expiry.",
      learn: [
        "Why the forward price is a no-arbitrage consequence, not a forecast",
        "How the cost of carry differs across stocks and indices, currencies and commodities",
        "What the convenience yield tells you",
      ],
      concepts: [
        "Buying the underlying today and holding it to expiry must cost the same as buying it forward. If it doesn't, there is an arbitrage.",
        "For an index, the cost of carry is the interest rate minus the dividend yield; for a currency it is the interest-rate differential between the two currencies.",
        "For commodities, storage costs also count, and so does the benefit of holding the physical good, called the convenience yield.",
      ],
      formula: "F_0 = S_0\\,e^{(r-q)T}",
      example: "Index at 100, interest rate 4%, dividend yield 2%, one-year expiry: F = 100 × e^0.02 ≈ 102.02. If the future traded well above that, buying the index and selling the future would pay; well below it, the reverse.",
      risks: "Confusing the theoretical forward with the market price. Transaction costs, securities-lending constraints and different borrowing and lending rates create a band in which arbitrage doesn't pay.",
      ref: "Hull, ch. 5",
      kb: { slug: "currency-hedging-hedged-vs-unhedged-etfs-and-how-to-analyze-them", label: "Currency hedging: hedged vs unhedged ETFs" },
    },
  },

  // ───────────────────────────── TASSI E SWAP ─────────────────────────────
  {
    n: 4, id: "ep-4", group: "tassi",
    // IT: episodio programmato su YouTube per il 21/11/2026 → da aggiungere dopo l'uscita.
    en: {
      videoId: "eHWIb1zlhDs",
      title: "Zero curve, forward rates and duration: pricing foundations",
      summary: "Discounting underpins every price: how the zero curve is built, what forward rates mean and how duration measures rate risk.",
      learn: [
        "How a zero-coupon curve is built from market prices",
        "Why forward rates are market-consistent prices, not forecasts",
        "What duration and convexity measure",
      ],
      concepts: [
        "A zero rate is the return on a single payment at a given date. Each cash flow is discounted at the zero rate for its own maturity.",
        "Forward rates are implied by today's curve: borrowing for two years must cost the same as borrowing for one year and rolling over at the forward rate.",
        "Duration measures price sensitivity to a small parallel shift in yields; convexity corrects it for larger moves.",
      ],
      formula: "R_F = \\frac{R_2 T_2 - R_1 T_1}{T_2 - T_1}",
      example: "With a one-year zero rate of 3% and a two-year zero rate of 3.5% (continuous compounding), the forward rate implied for the second year is (3.5% × 2 − 3% × 1) / 1 = 4.0%.",
      risks: "Reading forward rates as forecasts of future rates: they also contain risk premia. Discounting every cash flow at one single rate when the curve is not flat produces pricing errors.",
      ref: "Hull, ch. 4",
    },
  },
  {
    n: 6, id: "ep-6", group: "tassi",
    it: {
      videoId: "P0WmAHmud0g",
      title: "Futures su tassi: copertura con duration e DV01",
      summary: "Come si misura il rischio di tasso di un portafoglio obbligazionario e come lo si riduce con i futures sui titoli di Stato.",
      learn: [
        "Che cosa misurano duration e DV01",
        "Come si stima il numero di contratti per una copertura basata sulla duration",
        "Perché la copertura con i futures sui BTP lascia comunque un rischio di base",
      ],
      concepts: [
        "La duration modificata indica di quanto varia in percentuale il prezzo di un'obbligazione per una piccola variazione dei rendimenti.",
        "Il DV01 traduce la stessa sensibilità in euro: quanto cambia il valore del portafoglio per un punto base, lo 0,01%.",
        "Coprire vuol dire vendere i futures necessari a pareggiare il DV01 del portafoglio.",
        "Le convenzioni di calcolo dei giorni e il titolo più conveniente da consegnare spiegano perché il future non replica mai esattamente il portafoglio.",
      ],
      formula: "\\text{DV01} = P \\cdot D_{mod} \\cdot 0{,}0001 \\qquad N^{*} = \\frac{\\text{DV01}_{portafoglio}}{\\text{DV01}_{future}}",
      example: "L'esempio del video: portafoglio di BTP da 50 milioni con duration 7, quindi DV01 = 50.000.000 × 7 × 0,0001 = 35.000 € per punto base. Con un DV01 stimato di 126 € per contratto future sul BTP decennale servono 35.000 / 126 ≈ 278 contratti venduti, e il rischio netto scende a circa 28 € per punto base. Vendendone 200 si coprirebbe circa il 72%: è la copertura parziale discussa alla fine dell'episodio.",
      risks: "La copertura non è mai perfetta, per tre ragioni: il rischio di base tra i BTP in portafoglio e il future, il cambio del titolo più conveniente da consegnare (che modifica il DV01 del future) e i movimenti non paralleli della curva. Va ricalcolata e seguita nel tempo.",
      ref: "Hull, cap. 6",
    },
    en: {
      videoId: "h6ONQSmhZCc",
      title: "Interest rate futures hedging: duration and DV01",
      summary: "How to measure the interest-rate risk of a bond portfolio and how to reduce it with government bond futures.",
      learn: [
        "What duration and DV01 measure",
        "How to estimate the number of contracts for a duration-based hedge",
        "Why a bond futures hedge still leaves basis risk",
      ],
      concepts: [
        "Modified duration tells you the percentage change in a bond's price for a small change in yields.",
        "DV01 expresses the same sensitivity in money: how much the portfolio's value changes for one basis point, 0.01%.",
        "Hedging means selling enough futures to offset the portfolio's DV01.",
        "Day-count conventions and the cheapest-to-deliver bond explain why a future never replicates the portfolio exactly.",
      ],
      formula: "\\text{DV01} = P \\cdot D_{mod} \\cdot 0.0001 \\qquad N^{*} = \\frac{\\text{DV01}_{portfolio}}{\\text{DV01}_{futures}}",
      example: "A €10 million portfolio with a modified duration of 6 has a DV01 of €6,000 per basis point. If one contract has a DV01 of €90, about 67 contracts sold neutralise the rate risk, at least for small parallel shifts of the curve.",
      risks: "The hedge holds for small, parallel moves. If the curve changes slope, or the spread between the portfolio's bonds and those behind the future widens, it loses effectiveness.",
      ref: "Hull, ch. 6",
    },
  },
  {
    n: 7, id: "ep-7", group: "tassi",
    it: {
      videoId: "2EGjTBEcbhs",
      title: "Interest rate swap spiegato: flussi, valore, rischi",
      summary: "Lo swap su tassi scambia un tasso fisso contro uno variabile su un capitale di riferimento: come funziona e come si valuta.",
      learn: [
        "Come si scambiano i flussi in uno swap fisso contro variabile",
        "Perché il capitale nozionale non passa mai di mano",
        "Come si valuta uno swap attualizzando i flussi futuri",
      ],
      concepts: [
        "Chi paga fisso e riceve variabile trasforma un debito a tasso variabile in uno a tasso fisso; un fondo con obbligazioni a tasso fisso può fare l'operazione opposta.",
        "Alla firma lo swap vale circa zero. Poi vale la differenza tra i flussi da ricevere e quelli da pagare, stimati con i tassi forward della curva di oggi e attualizzati.",
        "Negli swap su valute il capitale si scambia davvero, all'inizio e alla fine. Servono anche a sfruttare il vantaggio comparato di due imprese che si finanziano meglio ciascuna nel proprio mercato.",
        "Il rischio di controparte si riduce con collaterale e netting. Il rischio di liquidità no: lo swap copre il rischio di tasso, non quello di finanziamento.",
      ],
      example: "L'esempio del video: swap a tre anni su un nozionale di 100 milioni. L'azienda paga il 5% fisso e riceve l'Euribor a sei mesi. Se l'Euribor fissato per il primo semestre è al 4%, l'azienda deve 2,5 milioni e ne riceve 2: paga la differenza netta, 500.000 €.",
      risks: "Il valore di uno swap cambia con le aspettative del mercato sui tassi, e chiuderlo prima della scadenza può costare caro. Il video ricorda il 2008: banche coperte correttamente sul rischio di tasso sono andate in crisi perché si finanziavano a breve.",
      ref: "Hull, cap. 7",
    },
    en: {
      videoId: "543nz8UggKk",
      title: "Interest rate swaps explained: cash flows, valuation, risks",
      summary: "An interest rate swap exchanges a fixed rate for a floating one on a reference amount: how it works and how it is valued.",
      learn: [
        "How cash flows are exchanged in a fixed-for-floating swap",
        "Why the notional principal never changes hands",
        "How a swap is valued by discounting future cash flows",
      ],
      concepts: [
        "Whoever pays fixed and receives floating is protected against rising rates, like a company with floating-rate debt.",
        "At inception a swap is worth roughly zero; afterwards its value moves with market rates.",
        "It is valued as the difference between two bonds, one fixed-rate and one floating-rate, or as a series of forward rate agreements.",
        "Counterparty and liquidity risk matter: a swap lasts years and is an OTC contract.",
      ],
      example: "Notional of 10 million, paying 3% fixed and receiving Euribor, with semi-annual payments. If Euribor for the period is 3.4%, the fixed payer receives the net difference: 10,000,000 × 0.4% × 0.5 = €20,000.",
      risks: "A swap turns floating debt into fixed, but if rates fall the position loses value and unwinding it before maturity can be expensive. The counterparty's soundness matters too.",
      ref: "Hull, ch. 7",
    },
  },

  // ───────────────────────────── OPZIONI ─────────────────────────────
  {
    n: 8, id: "ep-8", group: "opzioni",
    it: {
      videoId: "O0-6Tyn2X6s",
      title: "Opzioni call e put: payoff, rischio e come leggerle",
      summary: "Call e put danno un diritto, non un obbligo: payoff, premio, punto di pareggio e asimmetria rispetto ai futures.",
      learn: [
        "Che cosa sono call e put, e che differenza c'è tra comprarle e venderle",
        "Che cosa determina il premio: prezzo del sottostante, strike, scadenza, volatilità",
        "Perché si dice che chi compra un'opzione «compra convessità»",
      ],
      concepts: [
        "Chi compra una call ha il diritto di comprare a un prezzo fissato, lo strike; chi compra una put ha il diritto di vendere.",
        "Il compratore paga il premio e al massimo perde quello. Il venditore incassa il premio e si assume l'obbligo, con perdite potenzialmente molto più ampie.",
        "Il payoff è asimmetrico: un future guadagna e perde in modo lineare, un'opzione comprata ferma la perdita al premio.",
        "Il premio è fatto di valore intrinseco e di valore temporale, cioè il valore della possibilità che il prezzo si muova prima della scadenza.",
      ],
      formula: "\\text{call} = \\max(S_T - K,\\,0) \\qquad \\text{put} = \\max(K - S_T,\\,0)",
      example: "Call con strike 100 e premio 5: il pareggio è a 105. Se a scadenza il sottostante vale 120 il risultato è +15; se vale 90 si perde il premio, 5. Put con strike 100 e premio 4: pareggio a 96, e con il sottostante a 80 il risultato è +16.",
      risks: "Vendere opzioni senza copertura espone a perdite molto superiori al premio incassato, con richieste di margine da parte del broker. Il moltiplicatore del contratto è un dettaglio che si dimentica spesso.",
      ref: "Hull, cap. 10",
    },
    en: {
      videoId: "hT7S-ErQAaA",
      title: "Calls and puts: payoffs, risk and investor intuition",
      summary: "Calls and puts give a right, not an obligation: payoffs, premium, break-even and the asymmetry versus futures.",
      learn: [
        "What calls and puts are, and the difference between buying and selling them",
        "What drives the premium: underlying price, strike, time to expiry, volatility",
        "Why buying an option is described as «buying convexity»",
      ],
      concepts: [
        "A call buyer has the right to buy at a fixed price, the strike; a put buyer has the right to sell.",
        "The buyer pays the premium and can lose at most that. The seller collects the premium and takes on the obligation, with potentially much larger losses.",
        "The payoff is asymmetric: a future gains and loses linearly, a long option caps the loss at the premium.",
        "The premium is made of intrinsic value and time value, the value of the chance that the price moves before expiry.",
      ],
      formula: "\\text{call} = \\max(S_T - K,\\,0) \\qquad \\text{put} = \\max(K - S_T,\\,0)",
      example: "Call with strike 100 and premium 5: break-even is 105. If the underlying ends at 120 the result is +15; at 90 the premium is lost, −5. Put with strike 100 and premium 4: break-even at 96, and with the underlying at 80 the result is +16.",
      risks: "Writing naked options exposes you to losses far larger than the premium collected, with margin calls from the broker. The contract multiplier is a detail people often forget.",
      ref: "Hull, ch. 10",
    },
  },
  {
    n: 9, id: "ep-9", group: "opzioni",
    it: {
      videoId: "r2-IvtrxOmA",
      title: "Put-call parity e limiti di prezzo: l'antitruffa delle opzioni",
      summary: "Prima di qualsiasi modello esistono limiti logici al prezzo di un'opzione e una relazione che lega call e put.",
      learn: [
        "Quali sono i limiti superiori e inferiori al prezzo di call e put",
        "Come si usa la put-call parity per controllare la coerenza dei prezzi",
        "Come dividendi ed esercizio anticipato modificano queste relazioni",
      ],
      concepts: [
        "Una call europea non può valere più del sottostante, né meno della differenza tra il sottostante e lo strike attualizzato.",
        "Una call più un deposito pari allo strike attualizzato rendono a scadenza esattamente quanto una put più il sottostante. Quindi devono costare uguale.",
        "Se la relazione non torna c'è un arbitraggio, che i grandi operatori chiudono quasi subito.",
        "Con i dividendi il prezzo del sottostante va corretto per il loro valore attuale; per le opzioni americane la parità diventa una disuguaglianza.",
      ],
      formula: "c + K e^{-rT} = p + S_0",
      example: "Sottostante 100, strike 100, tasso 3%, un anno: lo strike attualizzato vale 97,04. Se la call vale 10, la put coerente vale 10 + 97,04 − 100 = 7,04. Il limite inferiore della call è 100 − 97,04 = 2,96.",
      risks: "Leggere la parità come un segnale operativo senza considerare costi di transazione, spread denaro-lettera e dividendi porta a vedere arbitraggi che non esistono.",
      ref: "Hull, cap. 11",
    },
    en: {
      videoId: "0qpBvEpZnDY",
      title: "Put-call parity and bounds: no-arbitrage checks",
      summary: "Before any model, there are logical limits to an option's price and a relationship that ties calls and puts together.",
      learn: [
        "The upper and lower bounds on call and put prices",
        "How to use put-call parity to check that prices are consistent",
        "How dividends and early exercise change these relationships",
      ],
      concepts: [
        "A European call cannot be worth more than the underlying, nor less than the underlying minus the discounted strike.",
        "A call plus a deposit equal to the discounted strike pays exactly the same at expiry as a put plus the underlying. So they must cost the same.",
        "If the relationship breaks there is an arbitrage, which large players close almost immediately.",
        "With dividends, the underlying price is adjusted by their present value; for American options parity becomes an inequality.",
      ],
      formula: "c + K e^{-rT} = p + S_0",
      example: "Underlying 100, strike 100, rate 3%, one year: the discounted strike is 97.04. If the call is worth 10, the consistent put is worth 10 + 97.04 − 100 = 7.04. The call's lower bound is 100 − 97.04 = 2.96.",
      risks: "Treating parity as a trading signal while ignoring transaction costs, bid-ask spreads and dividends leads you to see arbitrages that don't exist.",
      ref: "Hull, ch. 11",
    },
  },
  {
    n: 10, id: "ep-10", group: "opzioni",
    it: {
      videoId: "Uw31wckptk8",
      title: "Spread, straddle, covered call, protective put: strategie e rischi reali",
      summary: "Combinare più opzioni, o opzioni e sottostante, per dare forma al profilo di guadagni e perdite, con i rischi che ogni struttura nasconde.",
      learn: [
        "Come cambia il profilo di rischio con covered call e protective put",
        "Come uno spread limita insieme il costo e il guadagno",
        "Su che cosa scommettono straddle e strangle: la volatilità, non la direzione",
      ],
      concepts: [
        "Covered call: si possiede l'azione e si vende una call. Si incassa il premio, ma si rinuncia ai rialzi oltre lo strike e il ribasso resta quasi tutto.",
        "Protective put: si possiede l'azione e si compra una put. Il ribasso è limitato, al costo del premio pagato a ogni rinnovo.",
        "Bull spread: si compra una call e se ne vende un'altra con strike più alto. Costa meno, ma il guadagno ha un tetto.",
        "Straddle e strangle guadagnano se il prezzo si muove molto, in qualunque direzione, e perdono se resta fermo.",
      ],
      example: "Bull call spread con strike 95 e 105, premi 8 e 3: costo netto 5, guadagno massimo 5, perdita massima 5, pareggio a 100. Straddle con strike 100, call a 6 e put a 5: costo 11, pareggi a 89 e a 111.",
      risks: "Strategie che sembrano quasi sicure, come vendere opzioni per incassare premi, nascondono un rischio di coda: guadagni piccoli e frequenti, perdite rare ma ampie. Con più gambe, anche i costi di transazione pesano di più.",
      ref: "Hull, cap. 12",
    },
    en: {
      videoId: "L8_Kkew1JpI",
      title: "Spreads, straddles, covered calls, protective puts: the real risks",
      summary: "Combining options, or options and the underlying, to shape the profit-and-loss profile, along with the risks each structure hides.",
      learn: [
        "How covered calls and protective puts change the risk profile",
        "How a spread caps both cost and gain",
        "What straddles and strangles bet on: volatility, not direction",
      ],
      concepts: [
        "Covered call: own the stock and sell a call. You collect the premium but give up gains above the strike, and most of the downside remains.",
        "Protective put: own the stock and buy a put. The downside is limited, at the cost of the premium paid each time it is rolled.",
        "Bull spread: buy a call and sell another with a higher strike. It costs less, but the gain is capped.",
        "Straddles and strangles profit if the price moves a lot in either direction, and lose if it stays put.",
      ],
      example: "Bull call spread with strikes 95 and 105, premiums 8 and 3: net cost 5, maximum gain 5, maximum loss 5, break-even at 100. Straddle with strike 100, call at 6 and put at 5: cost 11, break-evens at 89 and 111.",
      risks: "Strategies that look almost safe, such as selling options to collect premiums, carry tail risk: small, frequent gains and rare, large losses. With more legs, transaction costs weigh more too.",
      ref: "Hull, ch. 12",
    },
  },

  // ───────────────────────────── MODELLI DI PREZZO ─────────────────────────────
  {
    n: 11, id: "ep-11", group: "pricing",
    it: {
      videoId: "BJDSB9_JEUU",
      title: "Albero binomiale: pricing e delta hedging passo per passo",
      summary: "Il modo più semplice per capire come si prezza un'opzione: costruire, con il sottostante, un portafoglio senza rischio.",
      learn: [
        "Come si costruisce un albero binomiale a uno e a più passi",
        "Che cos'è la valutazione neutrale al rischio",
        "Che cosa significa delta e come si usa per coprire un'opzione",
      ],
      concepts: [
        "Se il sottostante può solo salire o scendere di una certa misura, lo si può combinare con l'opzione in modo che il valore finale sia lo stesso nei due casi.",
        "Un portafoglio senza rischio deve rendere il tasso privo di rischio: da qui si ricava il prezzo dell'opzione.",
        "La probabilità «neutrale al rischio» non è una previsione. È lo strumento di calcolo che rende coerente il prezzo.",
        "Il delta è il numero di azioni da tenere per ogni opzione venduta. Cambia nel tempo, e la copertura va aggiustata.",
        "Per le opzioni americane, in ogni nodo si confronta il valore di continuazione con quello di esercizio immediato e si tiene il maggiore.",
      ],
      formula: "p = \\frac{e^{rT} - d}{u - d} \\qquad f = e^{-rT}\\left[\\,p\\,f_u + (1-p)\\,f_d\\,\\right]",
      example: "L'esempio del video, preso da Hull: azione a 20 € che fra tre mesi può valere 22 o 18, call con strike 21, tasso 12%. Delta = 1/4 = 0,25: con 0,25 azioni e una call venduta il portafoglio vale 4,50 € in entrambi i casi, cioè 4,367 € oggi, e la call vale 5 − 4,367 = 0,633 €. Con p = 0,6523 si ottiene lo stesso prezzo. Nel caso a due passi (azione a 100, strike 102, sei mesi, volatilità 20%) la call vale circa 5,68 € e il delta passa da 0,534 a 0,910 oppure a zero, secondo il percorso.",
      risks: "L'albero a un passo è un modello didattico. I risultati dipendono dal numero di passi e dalla scelta di u e d, che si legano alla volatilità del sottostante.",
      ref: "Hull, cap. 13",
    },
    en: {
      videoId: "lfG_cTLjIA8",
      title: "Binomial tree pricing and delta hedging, step by step",
      summary: "The simplest way to see how an option is priced: build a riskless portfolio with the underlying.",
      learn: [
        "How to build one-step and multi-step binomial trees",
        "What risk-neutral valuation is",
        "What delta means and how it is used to hedge an option",
      ],
      concepts: [
        "If the underlying can only move up or down by set amounts, it can be combined with the option so that the final value is the same in both cases.",
        "A riskless portfolio must earn the risk-free rate: that pins down the option price.",
        "The «risk-neutral» probability is not a forecast. It is the calculation device that makes the price consistent.",
        "Delta is the number of shares to hold for each option sold. It changes over time, so the hedge has to be adjusted.",
        "For American options, each node compares the continuation value with the value of exercising immediately and keeps the higher one.",
      ],
      formula: "p = \\frac{e^{rT} - d}{u - d} \\qquad f = e^{-rT}\\left[\\,p\\,f_u + (1-p)\\,f_d\\,\\right]",
      example: "The video's example: stock at $100, call with strike 100, six months split into two three-month steps, 20% volatility, 4% rate. Then u = 1.1052, d = 0.9048 and p ≈ 0.525. Final prices are 122.14, 100 and 81.87; working backwards, the call is worth about 11.52 at the up node and 5.99 today. The initial delta is 0.575: holding 0.575 shares per option sold leaves about $52.03 in both scenarios, and the gain on the $51.51 invested is exactly the risk-free rate for the quarter.",
      risks: "A one-step tree is a teaching model. Results depend on the number of steps and on the choice of u and d, which are tied to the underlying's volatility.",
      ref: "Hull, ch. 13",
    },
  },
  {
    n: 12, id: "ep-12", group: "pricing",
    it: {
      videoId: "ZX8FODy4-f4",
      title: "Random walk, Wiener e Itô: l'intuizione che porta a Black-Scholes",
      summary: "La matematica sotto i modelli di prezzo, raccontata per intuizione: processi stocastici, moto browniano e lemma di Itô.",
      learn: [
        "Che cos'è un processo di Wiener e perché l'incertezza cresce con la radice del tempo",
        "Che cosa descrive il moto browniano geometrico",
        "Perché il lemma di Itô serve per arrivare a Black-Scholes",
      ],
      concepts: [
        "Proprietà di Markov: per stimare il prezzo di domani conta solo quello di oggi, perché in un mercato efficiente il passato è già incorporato nel prezzo.",
        "In un processo di Wiener le variazioni su intervalli diversi sono indipendenti e la loro varianza cresce in proporzione al tempo.",
        "Nel moto browniano geometrico il rendimento ha una parte prevedibile, il drift, e una parte casuale proporzionale alla volatilità.",
        "Il lemma di Itô dice come cambia una funzione di una variabile casuale, per esempio il prezzo di un'opzione che dipende dal prezzo dell'azione.",
        "Ne segue che il logaritmo del prezzo ha distribuzione normale: i prezzi sono lognormali.",
      ],
      formula: "dS = \\mu S\\,dt + \\sigma S\\,dz \\qquad \\ln S_T \\sim \\mathcal{N}\\!\\left(\\ln S_0 + \\left(\\mu - \\tfrac{\\sigma^2}{2}\\right)T,\\ \\sigma^2 T\\right)",
      example: "Gli esempi del video: se dopo un anno la deviazione standard è 1, dopo cinque anni non è 5 ma √5 ≈ 2,24. E sulla crescita: 100 € che perdono il 20% diventano 80, e un successivo +20% li porta solo a 96; per tornare a 100 serve +25%. È la «zavorra» della volatilità che il lemma di Itô misura: il logaritmo del prezzo cresce a μ − σ²/2, non a μ.",
      risks: "Il video elenca tre trappole: credere che i grafici del passato prevedano il futuro, pensare che il rischio a dieci anni sia dieci volte quello a un anno, ignorare la correzione di Itô. Il modello stesso presuppone volatilità costante e movimenti continui: lo smile e i salti dei mercati veri ne mostrano i limiti.",
      ref: "Hull, cap. 14",
    },
    en: {
      videoId: "NxvU3jHLnNg",
      title: "Random walk, Wiener process and Itô: the bridge to Black-Scholes",
      summary: "The mathematics under pricing models, told through intuition: stochastic processes, Brownian motion and Itô's lemma.",
      learn: [
        "What a Wiener process is and why uncertainty grows with the square root of time",
        "What geometric Brownian motion describes",
        "Why Itô's lemma is needed to get to Black-Scholes",
      ],
      concepts: [
        "In a Wiener process, changes over different intervals are independent and their variance grows in proportion to time.",
        "In geometric Brownian motion, the return has a predictable part, the drift, and a random part proportional to volatility.",
        "Itô's lemma tells you how a function of a random variable changes, for instance the price of an option that depends on the stock price.",
        "It follows that the log of the price is normally distributed: prices are lognormal.",
      ],
      formula: "dS = \\mu S\\,dt + \\sigma S\\,dz \\qquad \\ln S_T \\sim \\mathcal{N}\\!\\left(\\ln S_0 + \\left(\\mu - \\tfrac{\\sigma^2}{2}\\right)T,\\ \\sigma^2 T\\right)",
      example: "With 20% annual volatility, the standard deviation over one month is about 20% × √(1/12) ≈ 5.8%, over one week about 2.8%. Doubling the horizon does not double the uncertainty: it multiplies it by √2.",
      risks: "The model assumes continuous moves and constant volatility. Real markets have jumps, fatter tails than the normal distribution and changing volatility: that is where the limits of Black-Scholes begin.",
      ref: "Hull, ch. 14",
    },
  },
  {
    n: 13, id: "ep-13", group: "pricing",
    it: {
      videoId: "cVILe5ihvSk",
      title: "Black-Scholes e volatilità implicita: che cosa misura davvero il mercato",
      summary: "La formula di Black-Scholes, che cosa assume e perché il mercato la usa al contrario per ricavare la volatilità implicita.",
      learn: [
        "Quali sono i cinque parametri del modello e quale non si osserva",
        "Come si legge la formula per una call europea",
        "Che cosa misura la volatilità implicita",
      ],
      concepts: [
        "Il modello poggia su un portafoglio senza rischio: con un delta di 0,4, comprare 40 azioni e vendere 100 call compensa i piccoli movimenti del prezzo.",
        "Prezzo del sottostante, strike, scadenza, tasso privo di rischio e volatilità: solo l'ultima va stimata.",
        "N(d₂) si può leggere come la probabilità, in un mondo neutrale al rischio, che la call scada in the money.",
        "Invertendo la formula a partire dal prezzo di mercato si ottiene la volatilità implicita, il modo in cui il mercato esprime il prezzo dell'incertezza.",
        "Se il modello fosse esatto la volatilità implicita sarebbe uguale per tutti gli strike. Nella realtà non lo è, e la forma che prende si chiama smile di volatilità.",
      ],
      formula: "c = S_0 N(d_1) - K e^{-rT} N(d_2) \\qquad d_{1,2} = \\frac{\\ln(S_0/K) + (r \\pm \\sigma^2/2)\\,T}{\\sigma\\sqrt{T}}",
      example: "L'esempio del video, preso da Hull: call quotata 1,875 $, azione a 21, strike 20, tasso 10%, tre mesi. Con volatilità al 20% la formula dà 1,76, troppo poco; con il 30% dà 2,10, troppo. Restringendo per tentativi si arriva al 23,5%: è la volatilità implicita in quel prezzo. Il VIX aggrega proprio volatilità implicite, quelle delle opzioni a 30 giorni sull'S&P 500.",
      risks: "La volatilità implicita non è una previsione certa: contiene anche il compenso che gli operatori chiedono per assumersi il rischio. Il modello ignora salti e costi di copertura.",
      ref: "Hull, cap. 15",
    },
    // EN: episodio programmato su YouTube per il 17/10/2026 → da aggiungere dopo l'uscita.
  },

  // ───────────────────────────── APPROFONDIMENTI ─────────────────────────────
  {
    n: null, id: "approfondimento-futures-opzioni", group: "deep",
    it: {
      videoId: "6CgqC2Na6xI",
      title: "Futures e opzioni: valutazione, arbitraggio e gestione del rischio",
      summary: "Un ripasso avanzato che mette a confronto futures e opzioni: come l'arbitraggio ne fissa il prezzo e come cambia il rischio che trasferiscono.",
      learn: [
        "Come l'arbitraggio cash-and-carry tiene il future vicino al valore teorico",
        "Perché il future ha un rischio lineare e l'opzione un rischio asimmetrico",
        "A che cosa servono le greche nel controllo del rischio",
      ],
      concepts: [
        "Future: obbligo reciproco, rischio lineare, prezzo legato al prezzo a pronti dal costo di carry.",
        "Opzione: diritto asimmetrico, perdita del compratore limitata al premio, prezzo legato alla volatilità.",
        "Le greche misurano le sensibilità: delta al prezzo, gamma alla variazione del delta, theta al passare del tempo, vega alla volatilità.",
        "Coprirsi con un future trasforma il rischio di prezzo in rischio di base; coprirsi con un'opzione costa un premio.",
      ],
      formula: "F_0 = S_0\\,e^{rT} \\quad (\\text{senza dividendi})",
      example: "Sottostante a 100, tasso 4%, scadenza a sei mesi, nessun dividendo: il future teorico vale circa 102,02. Se quotasse 103, comprare a pronti e vendere il future renderebbe più del tasso privo di rischio, e la differenza verrebbe chiusa in fretta.",
      risks: "È la puntata più densa della serie e funziona come ripasso: rende di più dopo aver visto le parti su futures e opzioni.",
      ref: "Hull, capp. 5, 10-13 e 19",
    },
    en: {
      videoId: "zUk1ja6pQGY",
      title: "Futures vs options: pricing, arbitrage and risk management",
      summary: "An advanced review comparing futures and options: how arbitrage sets their prices and how the risk they transfer differs.",
      learn: [
        "How cash-and-carry arbitrage keeps the future close to its theoretical value",
        "Why futures carry linear risk and options asymmetric risk",
        "What the Greeks are for in risk monitoring",
      ],
      concepts: [
        "Futures: mutual obligation, linear risk, price tied to spot through the cost of carry.",
        "Options: asymmetric right, the buyer's loss capped at the premium, price tied to volatility.",
        "The Greeks measure sensitivities: delta to price, gamma to changes in delta, theta to the passage of time, vega to volatility.",
        "Hedging with a future turns price risk into basis risk; hedging with an option costs a premium.",
      ],
      formula: "F_0 = S_0\\,e^{rT} \\quad (\\text{no dividends})",
      example: "Underlying at 100, rate 4%, six-month expiry, no dividends: the theoretical future is about 102.02. If it traded at 103, buying spot and selling the future would earn more than the risk-free rate, and the gap would close quickly.",
      risks: "The densest episode of the series, meant as a review: it pays off most after watching the futures and options episodes.",
      ref: "Hull, ch. 5, 10-13 and 19",
    },
  },
  {
    n: null, id: "approfondimento-certificati", group: "deep",
    it: {
      videoId: "ZiyVLk1EPCI",
      title: "Certificati e covered warrant: struttura, rischi e fiscalità",
      summary: "Due prodotti cartolarizzati che arrivano al risparmiatore: come sono costruiti, che rischi portano e come vengono tassati.",
      learn: [
        "La differenza di struttura: il certificato è un titolo di debito dell'emittente, il covered warrant un'opzione cartolarizzata",
        "Che cosa sono leva (gearing), barriere e premi periodici",
        "Perché la fiscalità dei certificati è diversa da quella di ETF e fondi",
      ],
      concepts: [
        "Entrambi dipendono dalla solidità di chi li emette: se l'emittente fallisce si può perdere anche quando il sottostante è andato bene.",
        "Nel covered warrant il valore temporale si consuma con il passare dei giorni, come in qualsiasi opzione.",
        "Un certificato con barriera protegge il capitale solo finché la barriera non viene toccata.",
        "I proventi dei certificati sono in genere redditi diversi e possono compensare minusvalenze pregresse, cosa che i proventi di ETF e fondi, redditi di capitale, non consentono.",
      ],
      example: "Certificato ipotetico con barriera al 60% osservata a scadenza: se l'azione sottostante ha perso il 30% si riceve il capitale nominale; se ha perso il 45% si subisce la stessa perdita dell'azione, cioè si riceve il 55%.",
      risks: "Struttura complessa, rischio emittente, prezzo di emissione che comprende i margini dell'emittente, liquidità affidata al market maker. Gli effetti fiscali dipendono dalla situazione personale di ciascuno.",
      ref: "Borsa Italiana · ACEPI",
      kb: { slug: "certificati-covered-warrant", label: "Certificati e covered warrant" },
    },
    en: {
      videoId: "ML_oEr8mYho",
      title: "Certificates vs covered warrants: structure, risks and tax",
      summary: "Two securitised products sold to retail investors in Italy: how they are built, what risks they carry and how they are taxed.",
      learn: [
        "The structural difference: a certificate is the issuer's debt security, a covered warrant is a securitised option",
        "What gearing, barriers and periodic coupons are",
        "Why, under Italian tax rules, certificates are treated differently from ETFs and funds",
      ],
      concepts: [
        "Both depend on the issuer's soundness: if the issuer defaults you can lose money even when the underlying has done well.",
        "In a covered warrant, time value erodes day by day, as in any option.",
        "A barrier certificate protects capital only as long as the barrier is not breached.",
        "In Italy, certificate income is generally «redditi diversi» and can offset past capital losses, which income from ETFs and funds («redditi di capitale») cannot.",
      ],
      example: "Hypothetical certificate with a 60% barrier observed at maturity: if the underlying stock has fallen 30%, you get the nominal amount back; if it has fallen 45%, you take the same loss as the stock and receive 55%.",
      risks: "Complex structure, issuer risk, an issue price that includes the issuer's margins, liquidity provided by a market maker. Tax effects depend on each person's circumstances.",
      ref: "Borsa Italiana · ACEPI",
      kb: { slug: "certificati-covered-warrant", label: "Certificates and covered warrants" },
    },
  },
];
